package screens.auth

import androidx.compose.runtime.*
import com.example.readingnotes.presentation.auth.AuthEvent
import com.example.readingnotes.presentation.auth.AuthViewModel
import design.AccessibilityGuidelines
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet
import utils.getSessionItem
import utils.setSessionItem

/**
 * OIDC Login Screen with Tab UI
 *
 * This screen provides a tab-based interface to switch between:
 * - Email login (existing functionality)
 * - OIDC login (authentication platform)
 *
 * Each tab maintains its own state independently.
 */
@Composable
fun OidcLoginScreen(
    onLoginSuccess: () -> Unit,
    onNavigateToAdmin: () -> Unit,
    authViewModel: AuthViewModel,
) {
    val state by authViewModel.state.collectAsState()
    var selectedTab by remember { mutableStateOf(LoginTab.EMAIL) }
    var oidcInitiated by remember { mutableStateOf(false) }

    // Navigate on successful login
    LaunchedEffect(state.isLoggedIn) {
        if (state.isLoggedIn) {
            delay(500)
            onLoginSuccess()
        }
    }

    // Handle OIDC redirect
    LaunchedEffect(state.oidcAuthorizationUrl) {
        if (state.oidcAuthorizationUrl != null && !oidcInitiated) {
            oidcInitiated = true
            // Store state and code_verifier in sessionStorage
            state.oidcState?.let { setSessionItem("oidc_state", it) }
            state.oidcCodeVerifier?.let { setSessionItem("oidc_code_verifier", it) }
            // Redirect to authorization server
            js("window.location.href = state.oidcAuthorizationUrl")
        }
    }

    // Announce errors to screen readers
    LaunchedEffect(state.errorMessage) {
        if (state.errorMessage != null && selectedTab == LoginTab.OIDC) {
            AccessibilityGuidelines.announceError(state.errorMessage!!)
        }
    }

    Div({ classes(AppStyleSheet.loginContainer) }) {
        Div({ classes(AppStyleSheet.loginForm) }) {
            H2({ classes(AppStyleSheet.loginTitle) }) {
                Text("ログイン")
            }

            P({
                style {
                    property("color", "#666")
                    property("margin-bottom", "20px")
                    property("font-size", "14px")
                }
            }) {
                Text("ログイン方法を選択してください")
            }

            // Tab Navigation
            Div({
                style {
                    property("display", "flex")
                    property("margin-bottom", "24px")
                    property("border-bottom", "1px solid #dee2e6")
                }
                attr("role", "tablist")
            }) {
                // Email Login Tab
                Button({
                    style {
                        property("flex", "1")
                        property("padding", "14px 16px")
                        // Use darker blue for selected tab to meet WCAG AA contrast ratio (4.5:1)
                        property("background-color", if (selectedTab == LoginTab.EMAIL) "#0056b3" else "transparent")
                        property("color", if (selectedTab == LoginTab.EMAIL) "white" else "#495057")
                        property("border", "none")
                        property("border-bottom", if (selectedTab == LoginTab.EMAIL) "2px solid #0056b3" else "2px solid transparent")
                        property("cursor", "pointer")
                        property("font-size", "14px")
                        property("font-weight", if (selectedTab == LoginTab.EMAIL) "600" else "400")
                        property("transition", "all 0.2s")
                        // Ensure minimum touch target size of 44x44px
                        property("min-height", "44px")
                    }
                    attr("role", "tab")
                    attr("aria-selected", if (selectedTab == LoginTab.EMAIL) "true" else "false")
                    attr("aria-controls", "email-login-panel")
                    attr("id", "email-login-tab")
                    attr("tabindex", if (selectedTab == LoginTab.EMAIL) "0" else "-1")
                    onClick {
                        selectedTab = LoginTab.EMAIL
                        oidcInitiated = false
                    }
                }) {
                    Text("メールでログイン")
                }

                // OIDC Login Tab
                Button({
                    style {
                        property("flex", "1")
                        property("padding", "14px 16px")
                        // Use darker blue for selected tab to meet WCAG AA contrast ratio (4.5:1)
                        property("background-color", if (selectedTab == LoginTab.OIDC) "#0056b3" else "transparent")
                        property("color", if (selectedTab == LoginTab.OIDC) "white" else "#495057")
                        property("border", "none")
                        property("border-bottom", if (selectedTab == LoginTab.OIDC) "2px solid #0056b3" else "2px solid transparent")
                        property("cursor", "pointer")
                        property("font-size", "14px")
                        property("font-weight", if (selectedTab == LoginTab.OIDC) "600" else "400")
                        property("transition", "all 0.2s")
                        // Ensure minimum touch target size of 44x44px
                        property("min-height", "44px")
                    }
                    attr("role", "tab")
                    attr("aria-selected", if (selectedTab == LoginTab.OIDC) "true" else "false")
                    attr("aria-controls", "oidc-login-panel")
                    attr("id", "oidc-login-tab")
                    attr("tabindex", if (selectedTab == LoginTab.OIDC) "0" else "-1")
                    onClick {
                        selectedTab = LoginTab.OIDC
                    }
                }) {
                    Text("認証基盤でログイン")
                }
            }

            // Tab Panels
            Div({
                style {
                    property("min-height", "300px")
                }
            }) {
                when (selectedTab) {
                    LoginTab.EMAIL -> EmailLoginTabContent(onNavigateToAdmin, authViewModel)
                    LoginTab.OIDC -> OidcLoginTabContent(authViewModel, state.isLoading)
                }
            }

            // Admin link
            P({
                style {
                    property("font-size", "12px")
                    property("color", "#666")
                    property("margin-top", "16px")
                    property("text-align", "center")
                }
            }) {
                Button({
                    style {
                        property("background", "none")
                        property("border", "none")
                        // Use darker blue to meet WCAG AA contrast ratio (4.5:1)
                        property("color", "#0056b3")
                        property("text-decoration", "underline")
                        property("cursor", "pointer")
                        property("font-size", "12px")
                    }
                    onClick { onNavigateToAdmin() }
                    attr("aria-label", "管理者ログイン画面へ移動")
                }) {
                    Text("管理者ログインはこちら")
                }
            }
        }
    }
}

/**
 * Email Login Tab Content
 * Reuses the existing EmailLoginScreen component
 */
@Composable
private fun EmailLoginTabContent(
    onNavigateToAdmin: () -> Unit,
    authViewModel: AuthViewModel,
) {
    Div({
        attr("role", "tabpanel")
        attr("id", "email-login-panel")
        attr("aria-labelledby", "email-login-tab")
        attr("tabindex", "0")
    }) {
        EmailLoginScreen(
            onLoginSuccess = { /* Handled by parent */ },
            onNavigateToLogin = { /* Already on login screen */ },
            authViewModel = authViewModel,
        )
    }
}

/**
 * OIDC Login Tab Content
 * Displays the OIDC login button with loading state
 */
@Composable
private fun OidcLoginTabContent(
    authViewModel: AuthViewModel,
    isLoading: Boolean,
) {
    Div({
        attr("role", "tabpanel")
        attr("id", "oidc-login-panel")
        attr("aria-labelledby", "oidc-login-tab")
        attr("tabindex", "0")
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("justify-content", "center")
            property("min-height", "300px")
        }
    }) {
        P({
            style {
                property("color", "#495057")
                property("margin-bottom", "24px")
                property("font-size", "14px")
                property("text-align", "center")
            }
        }) {
            Text("認証基盤のアカウントでログインします")
        }

        // OIDC Login Button
        Button({
            classes(AppStyleSheet.submitButton)
            style {
                property("min-height", "44px")
                property("width", "100%")
                property("max-width", "300px")
                property("font-size", "16px")
                property("font-weight", "600")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("gap", "8px")
            }
            attr("aria-label", "認証基盤でログイン")
            onClick {
                authViewModel.onEvent(AuthEvent.OnOidcLoginInitiate)
            }
            if (isLoading) {
                disabled()
            }
        }) {
            if (isLoading) {
                // Loading spinner
                Span({
                    style {
                        property("display", "inline-block")
                        property("width", "16px")
                        property("height", "16px")
                        property("border", "2px solid #ffffff")
                        property("border-top", "2px solid transparent")
                        property("border-radius", "50%")
                        property("animation", "spin 1s linear infinite")
                    }
                    attr("role", "status")
                    attr("aria-hidden", "true")
                }) {}
                Text("　処理中...")
            } else {
                // Authentication platform icon (generic key icon)
                Span({
                    style {
                        property("font-size", "20px")
                    }
                    attr("aria-hidden", "true")
                }) {
                    Text("🔑")
                }
                Text("認証基盤でログイン")
            }
        }

        // Help text
        P({
            style {
                property("font-size", "12px")
                property("color", "#6c757d")
                property("margin-top", "16px")
                property("text-align", "center")
            }
        }) {
            Text("クリックすると認証基盤のログインページにリダイレクトされます")
        }

        // Additional info
        Div({
            style {
                property("margin-top", "24px")
                property("padding", "12px")
                property("background-color", "#f8f9fa")
                property("border-radius", "4px")
                property("max-width", "400px")
                property("font-size", "12px")
                property("color", "#495057")
            }
            attr("role", "note")
            attr("aria-label", "補足情報")
        }) {
            Text("※ 認証基盤のアカウントをお持ちでない場合は、まず認証基盤でアカウントを作成してください")
        }
    }
}

/**
 * Enum representing login tabs
 */
private enum class LoginTab {
    EMAIL,
    OIDC,
}
